import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsArrowLeft, BsArrowRepeat, BsBoxArrowRight, BsChevronDown, BsDash, BsKey, BsPencil, BsPersonCircle, BsPlus, BsSearch, BsTrash } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import AdminRecordEditorModal from '../components/AdminRecordEditorModal';
import '../styles/Admin.css';

const DIRECTUS_URL = process.env.REACT_APP_DIRECTUS_URL || 'https://directus.saladesupreme.tarrieu.fr';
const TOKEN_STORAGE_KEY = 'salade-supreme-directus-token';
const EMAIL_STORAGE_KEY = 'salade-supreme-directus-email';
const COLLECTIONS = [
    { name: 'caphEvent', label: 'Événements', singular: 'événement' },
    { name: 'caphIntervenant', label: 'Intervenants', singular: 'intervenant' },
    { name: 'caphEventType', label: 'Types d’événement', singular: 'type d’événement' }
];

async function directusRequest(path, { token, method = 'GET', body } = {}) {
    const response = await fetch(`${DIRECTUS_URL}${path}`, {
        method,
        headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {})
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
        const directusErrors = payload.errors || [];
        const message = directusErrors.map((error) => error.message).join(', ');
        const error = new Error(message || `Erreur Directus (${response.status})`);
        error.status = response.status;
        error.code = directusErrors[0]?.extensions?.code;
        error.category = directusErrors[0]?.extensions?.category;
        throw error;
    }

    return payload.data;
}

function getAdminErrorMessage(error) {
    if (error.status === 403) {
        return 'Vous n’avez pas accès à ces ressources. Demandez à l’administrateur de vous accorder les autorisations nécessaires.';
    }
    return error.message || 'Une erreur est survenue lors de la récupération des ressources.';
}

function getAdminErrorDetails(error) {
    const status = error.status ? `HTTP ${error.status} · ` : '';
    return `${status}${error.message || 'Erreur Directus sans détail.'}`;
}

async function loadAdminUsers(token) {
    const users = await directusRequest('/users?fields=id,email,status,role.name&limit=-1&sort=email', { token });
    return users.filter((user) => ['Administrator', 'Edition'].includes(user.role?.name));
}

function getUserRoleLabel(user) {
    const roleName = typeof user.role === 'object' ? user.role?.name : user.role;
    if (roleName === 'Administrator') return 'Administrateur';
    if (roleName === 'Edition') return 'Edition';
    return roleName || 'Rôle inconnu';
}

function fieldLabel(field) {
    const label = field.meta?.translations?.find((translation) => translation.language?.startsWith('fr'))?.translation;
    if (label) return label;
    return String(field.meta?.field || field.field)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]/g, ' ')
        .replace(/^./, (letter) => letter.toUpperCase());
}

function getRecordLabel(record, fields = []) {
    const labelField = fields.find((field) => ['name', 'title', 'label'].includes(field.field))
        || fields.find((field) => field.field !== 'id');
    return labelField ? record[labelField.field] || `#${record.id}` : `#${record.id}`;
}

function getAdminRecordLabel(record, fields, collection) {
    return collection === 'caphEvent' ? record.title || 'Événement sans titre' : getRecordLabel(record, fields);
}

function normalizeSearchText(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function getRelationLabel(record, field, relations) {
    const relation = relations.find((item) => item.field === field);
    const value = record[field];
    if (value && typeof value === 'object') return getRecordLabel(value, relation?.fields);
    const relatedRecord = relation?.records.find((item) => String(item.id) === String(value));
    return relatedRecord ? getRecordLabel(relatedRecord, relation.fields) : '';
}

function getEventDateLabel(value) {
    if (!value) return '';
    const text = String(value);
    const [year, month, day] = text.slice(0, 10).split('-');
    if (!year || !month || !day) return text;
    const time = text.slice(11, 16);
    return `${day}/${month}/${year}${time ? ` à ${time}` : ''}`;
}

function isEventPast(record, now) {
    const value = record.endDate || record.startDate;
    if (!value) return false;
    const text = String(value);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(text)
        ? new Date(`${text}T23:59:59.999`)
        : new Date(text);
    return !Number.isNaN(date.getTime()) && date < now;
}

function Admin() {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [token, setToken] = useState(() => window.sessionStorage.getItem(TOKEN_STORAGE_KEY) || '');
    const [userEmail, setUserEmail] = useState(() => window.sessionStorage.getItem(EMAIL_STORAGE_KEY) || '');
    const [userRoleName, setUserRoleName] = useState('');
    const [currentUserId, setCurrentUserId] = useState('');
    const [adminUsers, setAdminUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [usersSuccess, setUsersSuccess] = useState('');
    const [userListVersion, setUserListVersion] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isUpdatingUserAccess, setIsUpdatingUserAccess] = useState(false);
    const [accessUpdateError, setAccessUpdateError] = useState('');
    const [isDeleteUserConfirmOpen, setIsDeleteUserConfirmOpen] = useState(false);
    const [deleteUserEmail, setDeleteUserEmail] = useState('');
    const [deleteUserError, setDeleteUserError] = useState('');
    const [isDeletingUser, setIsDeletingUser] = useState(false);
    const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
    const [newUser, setNewUser] = useState({ email: '', password: '' });
    const [createUserError, setCreateUserError] = useState('');
    const [createUserSuccess, setCreateUserSuccess] = useState('');
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [isOwnPasswordModalOpen, setIsOwnPasswordModalOpen] = useState(false);
    const [ownPassword, setOwnPassword] = useState({ password: '', confirmation: '' });
    const [ownPasswordError, setOwnPasswordError] = useState('');
    const [ownPasswordSuccess, setOwnPasswordSuccess] = useState('');
    const [isChangingOwnPassword, setIsChangingOwnPassword] = useState(false);
    const [activeAdminSection, setActiveAdminSection] = useState('content');
    const [activeCollection, setActiveCollection] = useState(COLLECTIONS[0].name);
    const [eventSearchQuery, setEventSearchQuery] = useState('');
    const [eventTypeFilter, setEventTypeFilter] = useState('');
    const [eventIntervenantFilter, setEventIntervenantFilter] = useState('');
    const [showPastEvents, setShowPastEvents] = useState(false);
    const [eventSortDirection, setEventSortDirection] = useState('asc');
    const [isEventFilterPanelOpen, setIsEventFilterPanelOpen] = useState(
        () => typeof window === 'undefined' || !window.matchMedia?.('(max-width: 760px)').matches
    );
    const [collectionData, setCollectionData] = useState({});
    const [loginError, setLoginError] = useState('');
    const [pageError, setPageError] = useState('');
    const [pageErrorDetails, setPageErrorDetails] = useState('');
    const [isPageErrorDetailsOpen, setIsPageErrorDetailsOpen] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLogoutConfirmationOpen, setIsLogoutConfirmationOpen] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editor, setEditor] = useState(null);
    const [isCollectionMenuOpen, setIsCollectionMenuOpen] = useState(false);
    const collectionMenuRef = useRef(null);

    useEffect(() => {
        if (!isCollectionMenuOpen) return undefined;

        const closeOnOutsideClick = (event) => {
            if (!collectionMenuRef.current?.contains(event.target)) {
                setIsCollectionMenuOpen(false);
            }
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setIsCollectionMenuOpen(false);
                collectionMenuRef.current?.querySelector('.admin-heading__menu-trigger')?.focus();
            }
        };

        document.addEventListener('pointerdown', closeOnOutsideClick);
        window.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('pointerdown', closeOnOutsideClick);
            window.removeEventListener('keydown', closeOnEscape);
        };
    }, [isCollectionMenuOpen]);

    const activeDefinition = COLLECTIONS.find((collection) => collection.name === activeCollection);
    const activeData = collectionData[activeCollection] || { fields: [], relations: [], records: [] };
    const eventRelations = activeData.relations;
    const eventTypes = eventRelations.find((relation) => relation.field === 'eventType')?.records || [];
    const eventIntervenants = eventRelations.find((relation) => relation.field === 'intervenant')?.records || [];
    const clearPageError = useCallback(() => {
        setPageError('');
        setPageErrorDetails('');
        setIsPageErrorDetailsOpen(false);
    }, []);
    const showPageError = useCallback((error) => {
        setPageError(getAdminErrorMessage(error));
        setPageErrorDetails(getAdminErrorDetails(error));
        setIsPageErrorDetailsOpen(false);
    }, []);
    const matchingEventRecords = useMemo(() => {
        const query = normalizeSearchText(eventSearchQuery.trim());
        const now = new Date();
        const filteredRecords = activeData.records.filter((record) => {
            if (!showPastEvents && isEventPast(record, now)) return false;
            const typeValue = record.eventType && typeof record.eventType === 'object' ? record.eventType.id : record.eventType;
            const intervenantValue = record.intervenant && typeof record.intervenant === 'object' ? record.intervenant.id : record.intervenant;
            if (eventTypeFilter && String(typeValue) !== eventTypeFilter) return false;
            if (eventIntervenantFilter && String(intervenantValue) !== eventIntervenantFilter) return false;
            if (!query) return true;

            return normalizeSearchText([
                record.title,
                record.description,
                record.startDate,
                record.endDate,
                record.link,
                getRelationLabel(record, 'eventType', eventRelations),
                getRelationLabel(record, 'intervenant', eventRelations)
            ].join(' ')).includes(query);
        });
        return filteredRecords.sort((first, second) => {
            const firstDate = new Date(first.startDate).getTime();
            const secondDate = new Date(second.startDate).getTime();
            const firstHasDate = !Number.isNaN(firstDate);
            const secondHasDate = !Number.isNaN(secondDate);
            if (firstHasDate !== secondHasDate) return firstHasDate ? -1 : 1;
            if (firstHasDate && firstDate !== secondDate) {
                return eventSortDirection === 'asc' ? firstDate - secondDate : secondDate - firstDate;
            }
            return String(first.id).localeCompare(String(second.id), 'fr', { numeric: true });
        });
    }, [activeData.records, eventIntervenantFilter, eventRelations, eventSearchQuery, eventSortDirection, eventTypeFilter, showPastEvents]);

    const expireSession = useCallback(() => {
        window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        window.sessionStorage.removeItem(EMAIL_STORAGE_KEY);
        setToken('');
        setUserEmail('');
        setUserRoleName('');
        setCurrentUserId('');
        setAdminUsers([]);
        setUsersSuccess('');
        setIsOwnPasswordModalOpen(false);
        setOwnPassword({ password: '', confirmation: '' });
        setOwnPasswordError('');
        setSelectedUser(null);
        setIsDeleteUserConfirmOpen(false);
        setIsCreateUserOpen(false);
        setNewUser({ email: '', password: '' });
        setCollectionData({});
        clearPageError();
        setLoginError('Votre session a expiré. Veuillez vous reconnecter.');
        setIsLogoutConfirmationOpen(false);
        setIsEditorOpen(false);
        setEditor(null);
    }, [clearPageError]);

    useEffect(() => {
        if (!token) return undefined;
        let isMounted = true;
        directusRequest('/users/me?fields=id,email,role.name', { token })
            .then((user) => {
                if (isMounted) {
                    setCurrentUserId(user?.id || '');
                    if (user?.email) {
                        setUserEmail(user.email);
                        window.sessionStorage.setItem(EMAIL_STORAGE_KEY, user.email);
                    }
                    setUserRoleName(user?.role?.name || '');
                }
            })
            .catch((error) => {
                if (isMounted && error.status === 401) expireSession();
            });

        return () => { isMounted = false; };
    }, [expireSession, token]);

    useEffect(() => {
        if (!token || activeAdminSection !== 'users' || userRoleName !== 'Administrator') return undefined;
        let isMounted = true;
        setIsLoadingUsers(true);
        setUsersError('');
        setUsersSuccess('');
        loadAdminUsers(token)
            .then((users) => { if (isMounted) setAdminUsers(users); })
            .catch((error) => {
                if (!isMounted) return;
                if (error.status === 401) expireSession();
                else setUsersError(getAdminErrorMessage(error));
            })
            .finally(() => { if (isMounted) setIsLoadingUsers(false); });

        return () => { isMounted = false; };
    }, [activeAdminSection, expireSession, token, userListVersion, userRoleName]);

    const loadCollection = useCallback(async (collectionName, accessToken) => {
        const [fields, relations, records] = await Promise.all([
            directusRequest(`/fields/${collectionName}`, { token: accessToken }),
            directusRequest('/relations?limit=-1', { token: accessToken }),
            directusRequest(`/items/${collectionName}?limit=200&sort=-id`, { token: accessToken })
        ]);

        const ownRelations = relations.filter((relation) => relation.collection === collectionName && relation.related_collection);
        const relationOptions = await Promise.all(ownRelations.map(async (relation) => {
            try {
                const relatedFields = await directusRequest(`/fields/${relation.related_collection}`, { token: accessToken });
                const relatedRecords = await directusRequest(`/items/${relation.related_collection}?limit=500`, { token: accessToken });
                return {
                    field: relation.field,
                    collection: relation.related_collection,
                    fields: relatedFields,
                    records: relatedRecords
                };
            } catch (error) {
                return { field: relation.field, collection: relation.related_collection, fields: [], records: [], error: getAdminErrorMessage(error) };
            }
        }));

        setCollectionData((current) => ({
            ...current,
            [collectionName]: { fields, relations: relationOptions, records }
        }));
    }, []);

    useEffect(() => {
        if (!token || activeAdminSection === 'users') return undefined;
        let isMounted = true;
        setIsLoading(true);
        clearPageError();

        loadCollection(activeCollection, token)
            .catch((error) => {
                if (error.status === 401) {
                    expireSession();
                } else {
                    showPageError(error);
                }
            })
            .finally(() => {
                if (isMounted) setIsLoading(false);
            });

        return () => { isMounted = false; };
    }, [activeAdminSection, activeCollection, clearPageError, expireSession, loadCollection, showPageError, token]);

    const handleLogin = async (event) => {
        event.preventDefault();
        setLoginError('');
        setIsLoggingIn(true);
        try {
            const session = await directusRequest('/auth/login', {
                method: 'POST',
                body: { email: credentials.email, password: credentials.password }
            });
            window.sessionStorage.setItem(TOKEN_STORAGE_KEY, session.access_token);
            window.sessionStorage.setItem(EMAIL_STORAGE_KEY, session.user?.email || credentials.email);
            setToken(session.access_token);
            setUserEmail(session.user?.email || credentials.email);
            setUserRoleName(session.user?.role?.name || '');
            setCurrentUserId(session.user?.id || '');
            setCredentials((current) => ({ ...current, password: '' }));
        } catch (error) {
            setLoginError(error.message || 'Connexion impossible. Vérifiez vos identifiants.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleOwnPasswordChange = async (event) => {
        event.preventDefault();
        if (userRoleName !== 'Edition') return;
        if (ownPassword.password !== ownPassword.confirmation) {
            setOwnPasswordError('Les deux mots de passe ne correspondent pas.');
            return;
        }

        setOwnPasswordError('');
        setOwnPasswordSuccess('');
        setIsChangingOwnPassword(true);
        try {
            await directusRequest('/users/me', {
                token,
                method: 'PATCH',
                body: { password: ownPassword.password }
            });
            setOwnPassword({ password: '', confirmation: '' });
            setIsOwnPasswordModalOpen(false);
            setOwnPasswordSuccess('Votre mot de passe a été modifié.');
        } catch (error) {
            if (error.status === 401) expireSession();
            else setOwnPasswordError(error.status === 403
                ? 'Vous n’avez pas l’autorisation de modifier votre mot de passe.'
                : error.message || 'La modification du mot de passe a échoué.');
        } finally {
            setIsChangingOwnPassword(false);
        }
    };

    const closeOwnPasswordModal = () => {
        setIsOwnPasswordModalOpen(false);
        setOwnPassword({ password: '', confirmation: '' });
        setOwnPasswordError('');
    };

    const handleCreateUser = async (event) => {
        event.preventDefault();
        if (userRoleName !== 'Administrator') {
            setCreateUserError('Seuls les utilisateurs avec le rôle Administrator peuvent créer des comptes.');
            return;
        }
        setCreateUserError('');
        setCreateUserSuccess('');
        setIsCreatingUser(true);
        try {
            const roleQuery = new URLSearchParams({
                'filter[name][_eq]': 'Edition',
                fields: 'id',
                limit: '1'
            });
            const roles = await directusRequest(`/roles?${roleQuery}`, { token });
            if (!roles?.[0]?.id) throw new Error('Le rôle « Edition » est introuvable dans Directus.');

            const email = newUser.email.trim();
            await directusRequest('/users', {
                token,
                method: 'POST',
                body: { email, password: newUser.password, role: roles[0].id }
            });
            setCreateUserSuccess(`Le compte ${email} a été créé avec le rôle Edition.`);
            setNewUser({ email: '', password: '' });
            setUserListVersion((version) => version + 1);
        } catch (error) {
            if (error.status === 401) {
                expireSession();
            } else if (error.category === 'seats' && error.code === 'LIMIT_EXCEEDED') {
                setCreateUserError('La limite de comptes Directus est atteinte. Libérez un siège ou augmentez le quota avant de créer un nouvel utilisateur.');
            } else if (error.status === 403) {
                setCreateUserError('Vous n’avez pas les autorisations Directus nécessaires pour créer un utilisateur.');
            } else {
                setCreateUserError(error.message || 'La création du compte a échoué.');
            }
        } finally {
            setIsCreatingUser(false);
        }
    };

    const closeCreateUser = () => {
        setIsCreateUserOpen(false);
        setNewUser({ email: '', password: '' });
        setCreateUserError('');
        setCreateUserSuccess('');
    };

    const handleUserAccessToggle = async () => {
        if (!selectedUser
            || getUserRoleLabel(selectedUser) !== 'Edition'
            || selectedUser.id === currentUserId
            || selectedUser.email.toLowerCase() === userEmail.toLowerCase()
            || userRoleName !== 'Administrator') return;
        setAccessUpdateError('');
        setIsUpdatingUserAccess(true);
        const status = selectedUser.status === 'active' ? 'suspended' : 'active';
        try {
            await directusRequest(`/users/${encodeURIComponent(selectedUser.id)}`, {
                token,
                method: 'PATCH',
                body: { status }
            });
            const updatedUser = { ...selectedUser, status };
            setSelectedUser(updatedUser);
            setAdminUsers((users) => users.map((user) => user.id === updatedUser.id ? updatedUser : user));
        } catch (error) {
            if (error.status === 401) expireSession();
            else setAccessUpdateError(error.status === 403
                ? 'Vous n’avez pas l’autorisation de modifier cet accès.'
                : error.message || 'La modification des droits a échoué.');
        } finally {
            setIsUpdatingUserAccess(false);
        }
    };

    const closeUserAccess = () => {
        setSelectedUser(null);
        setAccessUpdateError('');
    };

    const closeDeleteUserConfirmation = () => {
        setIsDeleteUserConfirmOpen(false);
        setDeleteUserEmail('');
        setDeleteUserError('');
    };

    const handleDeleteUser = async (event) => {
        event.preventDefault();
        if (!selectedUser
            || getUserRoleLabel(selectedUser) !== 'Edition'
            || selectedUser.id === currentUserId
            || userRoleName !== 'Administrator') return;
        if (deleteUserEmail.trim().toLowerCase() !== selectedUser.email.toLowerCase()) {
            setDeleteUserError('L’adresse saisie ne correspond pas à celle de cet utilisateur.');
            return;
        }

        setDeleteUserError('');
        setIsDeletingUser(true);
        try {
            await directusRequest(`/users/${encodeURIComponent(selectedUser.id)}`, { token, method: 'DELETE' });
            setAdminUsers((users) => users.filter((user) => user.id !== selectedUser.id));
            setUsersSuccess(`Le compte ${selectedUser.email} a été supprimé.`);
            setUserListVersion((version) => version + 1);
            setSelectedUser(null);
            closeDeleteUserConfirmation();
        } catch (error) {
            if (error.status === 401) expireSession();
            else setDeleteUserError(error.status === 403
                ? 'Vous n’avez pas l’autorisation de supprimer cet utilisateur.'
                : error.message || 'La suppression du compte a échoué.');
        } finally {
            setIsDeletingUser(false);
        }
    };

    const openEditor = (record = null) => {
        setEditor(record);
        setIsEditorOpen(true);
    };

    const closeEditor = () => {
        setIsEditorOpen(false);
        setEditor(null);
    };

    const handleDelete = async (record) => {
        const label = getAdminRecordLabel(record, activeData.fields, activeCollection);
        if (!window.confirm(`Supprimer « ${label} » ? Cette action est définitive.`)) return;
        clearPageError();
        try {
            await directusRequest(`/items/${activeCollection}/${encodeURIComponent(record.id)}`, { token, method: 'DELETE' });
            await loadCollection(activeCollection, token);
        } catch (error) {
            if (error.status === 401) expireSession();
            else showPageError(error);
        }
    };

    const logout = () => {
        window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        window.sessionStorage.removeItem(EMAIL_STORAGE_KEY);
        setToken('');
        setUserEmail('');
        setUserRoleName('');
        setCurrentUserId('');
        setSelectedUser(null);
        setIsOwnPasswordModalOpen(false);
        setOwnPassword({ password: '', confirmation: '' });
        setIsCreateUserOpen(false);
        setNewUser({ email: '', password: '' });
        setCollectionData({});
        clearPageError();
        setIsLogoutConfirmationOpen(false);
        closeEditor();
    };

    const refreshCollection = async () => {
        setIsLoading(true);
        clearPageError();
        try {
            await loadCollection(activeCollection, token);
        } catch (error) {
            if (error.status === 401) expireSession();
            else showPageError(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <main className="admin-login-page">
                <Link className="admin-back-link" to="/lieux/capharnaum"><BsArrowLeft aria-hidden="true" /> Retour au Capharnaüm</Link>
                <section className="admin-login">
                    <p className="admin-eyebrow">SALade SUPRÊME · ESPACE PRIVÉ</p>
                    <h1>Administration</h1>
                    <form onSubmit={handleLogin}>
                        <label className="admin-form__field" htmlFor="admin-email">
                            <span>Adresse e-mail</span>
                            <input id="admin-email" type="email" autoComplete="username" required value={credentials.email} onChange={(event) => setCredentials({ ...credentials, email: event.target.value })} />
                        </label>
                        <label className="admin-form__field" htmlFor="admin-password">
                            <span>Mot de passe</span>
                            <input id="admin-password" type="password" autoComplete="current-password" required value={credentials.password} onChange={(event) => setCredentials({ ...credentials, password: event.target.value })} />
                        </label>
                        {loginError && <p className="admin-error" role="alert">{loginError}</p>}
                        <button className="admin-button admin-button--primary admin-login__submit" type="submit" disabled={isLoggingIn}>
                            {isLoggingIn ? 'Connexion…' : 'Se connecter'}
                        </button>
                    </form>
                </section>
            </main>
        );
    }

    const displayFields = activeData.fields.filter((field) => field.field !== 'id' && !field.meta?.hidden);
    const primaryFields = ['title', 'name', 'startDate', 'description', 'bio'];
    const summaryFields = displayFields
        .filter((field) => primaryFields.includes(field.field))
        .slice(0, 3);
    const selectedUserIsAdministrator = selectedUser && getUserRoleLabel(selectedUser) === 'Administrateur';
    const selectedUserIsCurrent = selectedUser && (
        selectedUser.id === currentUserId
        || selectedUser.email.toLowerCase() === userEmail.toLowerCase()
    );
    const userGroups = [
        { name: 'Utilisateurs Edition', users: adminUsers.filter((user) => getUserRoleLabel(user) === 'Edition') },
        { name: 'Administrateurs', users: adminUsers.filter((user) => getUserRoleLabel(user) === 'Administrateur') }
    ];
    const selectAdminSection = (section) => {
        setIsCollectionMenuOpen(false);
        collectionMenuRef.current?.querySelector('.admin-heading__menu-trigger')?.focus();

        if (section === 'users') {
            setActiveAdminSection('users');
            setEditor(null);
            clearPageError();
            return;
        }

        setActiveAdminSection('content');
        setActiveCollection(section);
        setEditor(null);
        setEventSearchQuery('');
        setEventTypeFilter('');
        setEventIntervenantFilter('');
    };

    return (
        <main className="admin-app">
            <aside className="admin-sidebar">
                <div className="admin-sidebar__brand">
                    <span className="admin-sidebar__mark">S</span>
                    <span>Salade Suprême<small className="admin-sidebar__user"><BsPersonCircle aria-hidden="true" /><span>{userEmail || credentials.email}</span></small></span>
                </div>
                <div className="admin-heading__menu" ref={collectionMenuRef}>
                    <button
                        className="admin-heading__menu-trigger"
                        type="button"
                        aria-label={`Rubrique : ${activeAdminSection === 'users' ? 'Utilisateurs' : activeDefinition.label}`}
                        aria-expanded={isCollectionMenuOpen}
                        aria-controls="admin-heading-menu-options"
                        onClick={() => setIsCollectionMenuOpen((isOpen) => !isOpen)}
                    >
                        <span>{activeAdminSection === 'users' ? 'Utilisateurs' : activeDefinition.label}</span>
                        <BsChevronDown aria-hidden="true" />
                    </button>
                    {isCollectionMenuOpen && (
                        <div className="admin-heading__menu-options" id="admin-heading-menu-options" role="group" aria-label="Rubriques d’administration">
                            {COLLECTIONS.map((collection) => (
                                <button
                                    key={collection.name}
                                    type="button"
                                    className={activeAdminSection === 'content' && activeCollection === collection.name ? 'is-active' : ''}
                                    aria-pressed={activeAdminSection === 'content' && activeCollection === collection.name}
                                    onClick={() => selectAdminSection(collection.name)}
                                >
                                    {collection.label}
                                </button>
                            ))}
                            {['Administrator', 'Edition'].includes(userRoleName) && (
                                <button
                                    type="button"
                                    className={activeAdminSection === 'users' ? 'is-active' : ''}
                                    aria-pressed={activeAdminSection === 'users'}
                                    onClick={() => selectAdminSection('users')}
                                >
                                    Utilisateurs
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <p className="admin-sidebar__label">CONTENUS</p>
                <nav className="admin-collections" aria-label="Collections Directus">
                    {COLLECTIONS.map((collection) => (
                        <button
                            type="button"
                            key={collection.name}
                            className={activeAdminSection === 'content' && activeCollection === collection.name ? 'is-active' : ''}
                            onClick={() => selectAdminSection(collection.name)}
                        >
                            <span>{collection.label}</span>
                            <span className="admin-collections__count">{collectionData[collection.name]?.records.length ?? '—'}</span>
                        </button>
                    ))}
                    {['Administrator', 'Edition'].includes(userRoleName) && (
                        <button
                            type="button"
                            className={activeAdminSection === 'users' ? 'is-active' : ''}
                            onClick={() => selectAdminSection('users')}
                        >
                            <span>Utilisateurs</span>
                        </button>
                    )}
                </nav>
                <div className="admin-sidebar__bottom">
                    <Link className="admin-sidebar__action" to="/lieux/capharnaum#cours-ateliers" title="Voir le planning" aria-label="Voir le planning">
                        <BsArrowLeft aria-hidden="true" />
                    </Link>
                    <button className="admin-sidebar__action" type="button" onClick={() => setIsLogoutConfirmationOpen(true)} title="Se déconnecter" aria-label="Se déconnecter">
                        <BsBoxArrowRight aria-hidden="true" />
                    </button>
                    {activeAdminSection === 'content' && (
                        <button className="admin-sidebar__action" type="button" onClick={refreshCollection} title="Actualiser la collection" aria-label="Actualiser la collection">
                            <BsArrowRepeat aria-hidden="true" />
                        </button>
                    )}
                </div>
            </aside>

            <section className="admin-main">
                <div className="admin-content">
                    <div className="admin-heading">
                        <div>
                            <p className="admin-eyebrow">{activeAdminSection === 'users' ? 'CONTENUS · UTILISATEURS' : `COLLECTION · ${activeCollection}`}</p>
                            <h1>{activeAdminSection === 'users' ? 'Utilisateurs' : activeDefinition.label}</h1>
                            {activeAdminSection === 'users' && (
                                <p>{userRoleName === 'Administrator' ? 'Gestion des comptes' : 'Mon compte'}</p>
                            )}
                        </div>
                        <div className="admin-heading__actions">
                            {activeAdminSection === 'users' && userRoleName === 'Administrator' && (
                                <button
                                    type="button"
                                    className="admin-button admin-button--quiet"
                                    onClick={() => {
                                        setCreateUserError('');
                                        setCreateUserSuccess('');
                                        setIsCreateUserOpen(true);
                                    }}
                                >
                                    <BsPlus aria-hidden="true" /> Créer un utilisateur
                                </button>
                            )}
                            {activeAdminSection === 'users' && userRoleName === 'Edition' && (
                                <button
                                    type="button"
                                    className="admin-button admin-button--primary"
                                    onClick={() => {
                                        setOwnPasswordError('');
                                        setOwnPasswordSuccess('');
                                        setOwnPassword({ password: '', confirmation: '' });
                                        setIsOwnPasswordModalOpen(true);
                                    }}
                                >
                                    <BsKey aria-hidden="true" /> Modifier mon mot de passe
                                </button>
                            )}
                            {activeAdminSection === 'content' && (
                                <button
                                    type="button"
                                    className={`admin-button admin-button--primary${activeCollection === 'caphEvent' ? ' admin-button--event-add' : ''}`}
                                    onClick={() => openEditor()}
                                >
                                    <BsPlus aria-hidden="true" /> Ajouter {activeDefinition.singular}
                                </button>
                            )}
                        </div>
                    </div>

                    {activeAdminSection === 'users' ? (
                        <>
                            <div className="admin-users-guidance">
                                {userRoleName === 'Administrator' ? (
                                    <p>
                                        Le rôle Administrateur permet de créer des comptes Edition.
                                        Ces comptes peuvent modifier les informations du planning, mais pas créer d’autres utilisateurs.
                                        Pour des raisons de sécurité, utilisez toujours un compte personnel, jamais
                                        <strong>mediation@saladesupreme.fr</strong>.
                                    </p>
                                ) : (
                                    <p>Votre compte vous permet de modifier les informations du planning.</p>
                                )}
                                {userRoleName === 'Administrator' && <p>Cliquez sur un utilisateur pour autoriser ou retirer son accès.</p>}
                            </div>
                            {ownPasswordSuccess && userRoleName === 'Edition' && <p className="admin-user-form__success" role="status">{ownPasswordSuccess}</p>}
                            {userRoleName === 'Administrator' && usersError && <p className="admin-error admin-error--panel" role="alert">{usersError}</p>}
                            {userRoleName === 'Administrator' && usersSuccess && <p className="admin-user-form__success" role="status">{usersSuccess}</p>}
                            {userRoleName === 'Administrator' && isLoadingUsers && <p className="admin-status" role="status">Chargement des utilisateurs…</p>}
                            {userRoleName === 'Administrator' && !isLoadingUsers && !usersError && adminUsers.length === 0 && <p className="admin-status">Aucun utilisateur à afficher.</p>}
                            {userRoleName === 'Administrator' && !isLoadingUsers && adminUsers.length > 0 && (
                                <div className="admin-user-list" aria-label="Utilisateurs et rôles">
                                    <div className="admin-user-list__header" aria-hidden="true">
                                        <span>Utilisateur</span>
                                        <span>Rôle</span>
                                        <span>Accès</span>
                                    </div>
                                    {userGroups.map((group) => group.users.length > 0 && (
                                        <section className="admin-user-group" key={group.name}>
                                            {group.users.map((user) => (
                                                <button
                                                    type="button"
                                                    className="admin-user-row"
                                                    key={user.id}
                                                    onClick={() => { setSelectedUser(user); setAccessUpdateError(''); }}
                                                    aria-label={`Gérer l’accès de ${user.email}, rôle ${getUserRoleLabel(user)}`}
                                                >
                                                    <span className="admin-user-row__email">{user.email}</span>
                                                    <span className={`admin-user-row__role${getUserRoleLabel(user) === 'Administrateur' ? ' is-administrator' : ' is-edition'}`}>{getUserRoleLabel(user)}</span>
                                                    <span className={`admin-user-row__status${user.status === 'active' ? ' is-active' : ''}`}>
                                                        {user.status === 'active' ? 'Accès actif' : 'Accès suspendu'}
                                                    </span>
                                                </button>
                                            ))}
                                        </section>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {pageError && (
                                <div className="admin-error admin-error--panel" role="alert">
                                    <p>{pageError}</p>
                                    {pageErrorDetails && (
                                        <>
                                            <button
                                                type="button"
                                                className="admin-error__toggle"
                                                aria-expanded={isPageErrorDetailsOpen}
                                                onClick={() => setIsPageErrorDetailsOpen((isOpen) => !isOpen)}
                                            >
                                                {isPageErrorDetailsOpen ? <BsDash aria-hidden="true" /> : <BsPlus aria-hidden="true" />}
                                                Détails
                                            </button>
                                            {isPageErrorDetailsOpen && <pre className="admin-error__details">{pageErrorDetails}</pre>}
                                        </>
                                    )}
                                </div>
                            )}
                            {isLoading ? <p className="admin-status">Chargement des données et du modèle Directus…</p> : null}
                            {!isLoading && !pageError && activeData.fields.length === 0 && <p className="admin-status">Aucun modèle accessible pour cette collection.</p>}

                            {activeCollection === 'caphEvent' && (
                                <>
                                    <label className="admin-event-search">
                                        <BsSearch aria-hidden="true" />
                                        <input
                                            type="search"
                                            value={eventSearchQuery}
                                            onChange={(event) => setEventSearchQuery(event.target.value)}
                                            placeholder="Rechercher un événement…"
                                            aria-label="Rechercher un événement"
                                        />
                                    </label>
                                    <details
                                        className="admin-event-filter-panel"
                                        open={isEventFilterPanelOpen}
                                        onToggle={(event) => setIsEventFilterPanelOpen(event.currentTarget.open)}
                                    >
                                        <summary>Filtres <BsChevronDown aria-hidden="true" /></summary>
                                        <div className="admin-event-controls">
                                        <label className="admin-event-filter">
                                            <span>Type</span>
                                            <select value={eventTypeFilter} onChange={(event) => setEventTypeFilter(event.target.value)}>
                                                <option value="">Tous les types</option>
                                                {eventTypes.map((type) => <option key={type.id} value={type.id}>{getRecordLabel(type, eventRelations.find((relation) => relation.field === 'eventType')?.fields)}</option>)}
                                            </select>
                                        </label>
                                        <label className="admin-event-filter">
                                            <span>Intervenant</span>
                                            <select value={eventIntervenantFilter} onChange={(event) => setEventIntervenantFilter(event.target.value)}>
                                                <option value="">Tous les intervenants</option>
                                                {eventIntervenants.map((intervenant) => <option key={intervenant.id} value={intervenant.id}>{getRecordLabel(intervenant, eventRelations.find((relation) => relation.field === 'intervenant')?.fields)}</option>)}
                                            </select>
                                        </label>
                                        <label className="admin-event-past-toggle">
                                            <input
                                                type="checkbox"
                                                role="switch"
                                                checked={showPastEvents}
                                                onChange={(event) => setShowPastEvents(event.target.checked)}
                                            />
                                            <span>Afficher les événements passés</span>
                                        </label>
                                        <label className="admin-event-filter">
                                            <span>Ordre chronologique</span>
                                            <select value={eventSortDirection} onChange={(event) => setEventSortDirection(event.target.value)}>
                                                <option value="asc">Du plus ancien au plus récent</option>
                                                <option value="desc">Du plus récent au plus ancien</option>
                                            </select>
                                        </label>
                                        </div>
                                    </details>
                                </>
                            )}

                            <div className="admin-record-list" aria-live="polite">
                                {(activeCollection === 'caphEvent' ? matchingEventRecords : activeData.records).map((record) => (
                                    <article className="admin-record" key={record.id}>
                                        <div className="admin-record__identity">
                                            {activeCollection !== 'caphEvent' && <span className="admin-record__id">#{record.id}</span>}
                                            <div>
                                                <h2>{getAdminRecordLabel(record, activeData.fields, activeCollection)}</h2>
                                                <p>{activeCollection === 'caphEvent'
                                                    ? [getEventDateLabel(record.startDate), getRelationLabel(record, 'eventType', eventRelations), getRelationLabel(record, 'intervenant', eventRelations), record.description].filter(Boolean).join('  /  ').slice(0, 180)
                                                    : summaryFields.filter((field) => record[field.field] && field.field !== (activeData.fields.find((item) => ['title', 'name', 'label'].includes(item.field))?.field)).map((field) => `${fieldLabel(field)} · ${String(record[field.field]).slice(0, 90)}`).join('  /  ') || activeCollection}</p>
                                            </div>
                                        </div>
                                        <div className="admin-record__actions">
                                            <button type="button" className="admin-icon-button" title="Modifier" aria-label={`Modifier ${getAdminRecordLabel(record, activeData.fields, activeCollection)}`} onClick={() => openEditor(record)}><BsPencil aria-hidden="true" /></button>
                                            <button type="button" className="admin-icon-button admin-icon-button--danger" title="Supprimer" aria-label={`Supprimer ${getAdminRecordLabel(record, activeData.fields, activeCollection)}`} onClick={() => handleDelete(record)}><BsTrash aria-hidden="true" /></button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                            {activeCollection === 'caphEvent' && matchingEventRecords.length === 0 && activeData.records.length > 0 && (
                                <p className="admin-event-search__empty" role="status">
                                    {!showPastEvents && !eventSearchQuery.trim() && !eventTypeFilter && !eventIntervenantFilter
                                        ? 'Aucun événement à venir. Activez « Afficher les événements passés » pour consulter l’historique.'
                                        : 'Aucun événement ne correspond à ces critères.'}
                                </p>
                            )}
                            {!isLoading && !pageError && activeData.records.length === 0 && activeData.fields.length > 0 && (
                                <div className="admin-empty"><span>0{activeCollection === 'caphEvent' ? '1' : '2'}</span><p>Aucune entrée pour le moment.</p><button type="button" onClick={() => openEditor()}>Créer la première</button></div>
                            )}
                        </>
                    )}
                </div>
            </section>

            {isOwnPasswordModalOpen && userRoleName === 'Edition' && (
                <div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !isChangingOwnPassword) closeOwnPasswordModal(); }}>
                    <section className="admin-dialog admin-dialog--logout admin-dialog--user-create" role="dialog" aria-modal="true" aria-labelledby="admin-own-password-title">
                        <h2 id="admin-own-password-title">Modifier mon mot de passe</h2>
                        <p>Compte : <strong>{userEmail}</strong></p>
                        <form className="admin-user-form" onSubmit={handleOwnPasswordChange}>
                            <label className="admin-form__field" htmlFor="admin-own-new-password">
                                <span>Nouveau mot de passe</span>
                                <input
                                    id="admin-own-new-password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={ownPassword.password}
                                    onChange={(event) => setOwnPassword((current) => ({ ...current, password: event.target.value }))}
                                />
                            </label>
                            <label className="admin-form__field" htmlFor="admin-own-new-password-confirmation">
                                <span>Confirmer le mot de passe</span>
                                <input
                                    id="admin-own-new-password-confirmation"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={ownPassword.confirmation}
                                    onChange={(event) => setOwnPassword((current) => ({ ...current, confirmation: event.target.value }))}
                                />
                            </label>
                            {ownPasswordError && <p className="admin-error" role="alert">{ownPasswordError}</p>}
                            <footer className="admin-dialog__footer">
                                <button type="button" className="admin-button admin-button--quiet" onClick={closeOwnPasswordModal} disabled={isChangingOwnPassword}>Annuler</button>
                                <button type="submit" className="admin-button admin-button--primary" disabled={isChangingOwnPassword}>
                                    {isChangingOwnPassword ? 'Modification…' : 'Enregistrer le mot de passe'}
                                </button>
                            </footer>
                        </form>
                    </section>
                </div>
            )}

            {isCreateUserOpen && (
                <div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !isCreatingUser) closeCreateUser(); }}>
                    <section className="admin-dialog admin-dialog--logout admin-dialog--user-create" role="dialog" aria-modal="true" aria-labelledby="admin-create-user-title">
                        <h2 id="admin-create-user-title">Créer un utilisateur</h2>
                        <p className="admin-user-form__note">Le compte sera associé au rôle « Edition ».</p>
                        <form className="admin-user-form" onSubmit={handleCreateUser}>
                            <label className="admin-form__field" htmlFor="admin-new-user-email">
                                <span>Adresse e-mail</span>
                                <input
                                    id="admin-new-user-email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={newUser.email}
                                    onChange={(event) => setNewUser((current) => ({ ...current, email: event.target.value }))}
                                />
                            </label>
                            <label className="admin-form__field" htmlFor="admin-new-user-password">
                                <span>Mot de passe</span>
                                <input
                                    id="admin-new-user-password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={newUser.password}
                                    onChange={(event) => setNewUser((current) => ({ ...current, password: event.target.value }))}
                                />
                            </label>
                            {createUserError && <p className="admin-error" role="alert">{createUserError}</p>}
                            {createUserSuccess && <p className="admin-user-form__success" role="status">{createUserSuccess}</p>}
                            <footer className="admin-dialog__footer">
                                <button type="button" className="admin-button admin-button--quiet" onClick={closeCreateUser} disabled={isCreatingUser}>Annuler</button>
                                <button type="submit" className="admin-button admin-button--primary" disabled={isCreatingUser}>
                                    {isCreatingUser ? 'Création…' : 'Créer le compte'}
                                </button>
                            </footer>
                        </form>
                    </section>
                </div>
            )}

            {selectedUser && (
                <div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !isUpdatingUserAccess) closeUserAccess(); }}>
                    <section className="admin-dialog admin-dialog--logout admin-dialog--user-create" role="dialog" aria-modal="true" aria-labelledby="admin-user-access-title">
                        <h2 id="admin-user-access-title">Accès utilisateur</h2>
                        <p>{selectedUser.email}</p>
                        <p className="admin-user-access__role">Rôle : {getUserRoleLabel(selectedUser)}</p>
                        {selectedUserIsAdministrator ? (
                            <p className="admin-user-access__note">Les accès des comptes Administrateur ne peuvent pas être retirés.</p>
                        ) : selectedUserIsCurrent ? (
                            <p className="admin-user-access__note">Vous ne pouvez pas retirer l’accès du compte utilisé actuellement.</p>
                        ) : (
                            <label className="admin-user-access-toggle">
                                <span>Accès autorisé</span>
                                <input
                                    type="checkbox"
                                    role="switch"
                                    checked={selectedUser.status === 'active'}
                                    disabled={isUpdatingUserAccess}
                                    onChange={handleUserAccessToggle}
                                />
                            </label>
                        )}
                        {accessUpdateError && <p className="admin-error" role="alert">{accessUpdateError}</p>}
                        <footer className="admin-dialog__footer">
                            {!selectedUserIsAdministrator && !selectedUserIsCurrent && (
                                <button
                                    type="button"
                                    className="admin-button admin-button--quiet admin-user-access__delete"
                                    onClick={() => {
                                        setDeleteUserEmail('');
                                        setDeleteUserError('');
                                        setIsDeleteUserConfirmOpen(true);
                                    }}
                                    disabled={isUpdatingUserAccess}
                                >
                                    Supprimer l’utilisateur
                                </button>
                            )}
                            <button type="button" className="admin-button admin-button--quiet" onClick={closeUserAccess} disabled={isUpdatingUserAccess}>Fermer</button>
                        </footer>
                    </section>
                </div>
            )}

            {isDeleteUserConfirmOpen && selectedUser && (
                <div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !isDeletingUser) closeDeleteUserConfirmation(); }}>
                    <section className="admin-dialog admin-dialog--logout admin-dialog--user-create" role="dialog" aria-modal="true" aria-labelledby="admin-delete-user-title">
                        <h2 id="admin-delete-user-title">Confirmer la suppression</h2>
                        <p>Cette action est définitive. Saisissez l’adresse e-mail de <strong>{selectedUser.email}</strong> pour confirmer.</p>
                        <form className="admin-user-form" onSubmit={handleDeleteUser}>
                            <label className="admin-form__field" htmlFor="admin-delete-user-email">
                                <span>Adresse e-mail à confirmer</span>
                                <input
                                    id="admin-delete-user-email"
                                    type="email"
                                    autoComplete="off"
                                    required
                                    value={deleteUserEmail}
                                    onChange={(event) => setDeleteUserEmail(event.target.value)}
                                />
                            </label>
                            {deleteUserError && <p className="admin-error" role="alert">{deleteUserError}</p>}
                            <footer className="admin-dialog__footer">
                                <button type="button" className="admin-button admin-button--quiet" onClick={closeDeleteUserConfirmation} disabled={isDeletingUser}>Annuler</button>
                                <button
                                    type="submit"
                                    className="admin-button admin-button--primary admin-user-delete-button"
                                    disabled={isDeletingUser || deleteUserEmail.trim().toLowerCase() !== selectedUser.email.toLowerCase()}
                                >
                                    {isDeletingUser ? 'Suppression…' : 'Supprimer définitivement'}
                                </button>
                            </footer>
                        </form>
                    </section>
                </div>
            )}

            {isEditorOpen ? (
                <AdminRecordEditorModal
                    key={`${activeCollection}-${editor?.id || 'new'}`}
                    collection={activeCollection}
                    collectionLabel={activeDefinition.label}
                    singular={activeDefinition.singular}
                    token={token}
                    record={editor}
                    fields={activeData.fields}
                    relations={activeData.relations}
                    onClose={closeEditor}
                    onSaved={() => loadCollection(activeCollection, token)}
                    onUnauthorized={expireSession}
                />
            ) : null}

            {isLogoutConfirmationOpen && (
                <div className="admin-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setIsLogoutConfirmationOpen(false); }}>
                    <section className="admin-dialog admin-dialog--logout" role="dialog" aria-modal="true" aria-labelledby="admin-logout-title">
                        <h2 id="admin-logout-title">Confirmer la déconnexion</h2>
                        <p>Êtes-vous sûr de vouloir vous déconnecter de l’administration ?</p>
                        <footer className="admin-dialog__footer">
                            <button type="button" className="admin-button admin-button--quiet" onClick={() => setIsLogoutConfirmationOpen(false)}>Annuler</button>
                            <button type="button" className="admin-button admin-button--primary" onClick={logout}>Se déconnecter</button>
                        </footer>
                    </section>
                </div>
            )}
        </main>
    );
}

export default Admin;